import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getProducts } from '../api/api';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import TrustBadges from '../components/TrustBadges';
import EditorialFeatures from '../components/EditorialFeatures';
import CultureCards from '../components/CultureCards';
import { hasSale, getSalePrice, withEffectivePrice } from '../utils/sale';
import '../styles/designSystem.css';
import './Home.css';

function Home() {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { isShopper } = useAuth();
  const navigate = useNavigate();
  const { cartItems, addToCart } = useCart();

  const sliderProducts = featuredProducts.length > 0 ? [...featuredProducts, ...featuredProducts] : [];

  useEffect(() => {
    fetchFeaturedProducts();
  }, []);

  const fetchFeaturedProducts = async () => {
    try {
      const response = await getProducts({ featured: true });
      // Backend returns { products: [...], pagination: {...} }
      const products = response.data.products || [];
      setFeaturedProducts(products.slice(0, 6));
    } catch (error) {
      console.error('Error fetching featured products:', error);
      setFeaturedProducts([]);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to get image URL
  const getImageUrl = (image) => {
    if (!image) return '/placeholder.jpg';
    const url = typeof image === 'string' ? image : image.url;
    return url.startsWith('http') ? url : `${window.location.origin}/.netlify/functions/api${url}`;
  };

  // Get quantity of product in cart
  const getCartQuantity = (productId) => {
    const item = cartItems.find(item => item._id === productId);
    return item ? item.quantity : 0;
  };

  // Handle add to cart with click stop propagation
  const handleAddToCart = (e, product) => {
    e.preventDefault();
    e.stopPropagation();
    addToCart(withEffectivePrice(product), 1);
  };

  return (
    <div className="home">
      {/* ===== FEATURED PRODUCTS - Editor's Picks ===== */}
      <section className="featured-products section">
        <div className="container">
          <div className="featured-products__header text-center">
            <h2>Featured Products</h2>
            <p className="featured-products__subtitle">
              Curated collections celebrating South African heritage
            </p>
          </div>

          {loading ? (
            <p className="text-center" style={{ padding: '2rem' }}>
              Loading featured pieces...
            </p>
          ) : featuredProducts.length > 0 ? (
            <div className="featured-products__slider">
              <div className="featured-products__track">
                {sliderProducts.map((product, index) => {
                  const cartQuantity = getCartQuantity(product._id);
                  return (
                    <Link
                      to={`/products/${product._id}`}
                      key={`${product._id}-${index}`}
                      className="product-card product-card--slider"
                    >
                      <div className="product-card__image-wrapper">
                        <img
                          src={getImageUrl(product.images && product.images[0])}
                          alt={product.name}
                          className="product-card__image"
                        />
                        {product.featured && (
                          <span className="product-card__badge">Editor's Pick</span>
                        )}
                        {hasSale(product) && (
                          <span className="product-card__badge product-card__badge--sale">SALE {product.salePercentage}% OFF</span>
                        )}
                        {cartQuantity > 0 && (
                          <span className="product-card__cart-badge">{cartQuantity}</span>
                        )}
                      </div>
                      <div className="product-card__content">
                        <h3 className="product-card__name">{product.name}</h3>
                        {product.sku && <p className="product-card__sku">SKU: {product.sku}</p>}
                        <p className="product-card__culture">
                          {product.culturalOrigin || product.culture}
                          {product.category && ` • ${product.category}`}
                        </p>
                        {hasSale(product) ? (
                          <p className="product-card__price">
                            <span className="product-card__price--original">R{(product.price || 0).toFixed(2)}</span>
                            {' '}
                            <span className="product-card__price--sale">R{getSalePrice(product).toFixed(2)}</span>
                          </p>
                        ) : (
                          <p className="product-card__price">R{(product.price || 0).toFixed(2)}</p>
                        )}
                        <button
                          className="product-card__cart-btn"
                          onClick={(e) => handleAddToCart(e, product)}
                          title="Add to cart"
                        >
                          🛒
                        </button>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
          ) : (
            <p className="text-center" style={{ padding: '2rem', color: '#999' }}>
              No featured products available.
            </p>
          )}

          <div className="featured-products__footer text-center mt-xl">
            <Link to="/products" className="btn btn-outline">
              View All Products
            </Link>
          </div>
        </div>
      </section>

      {/* ===== HERO SECTION - Editorial Storytelling ===== */}
      <section className="hero">
        <div className="container">
          <div className="hero__content">
            <h1 className="hero__headline">Wear the story. Gift the tradition.</h1>
            <p className="hero__subtitle">
              Authentic cultural wear & accessories. Delivered nationwide.
            </p>
            <div className="hero__ctas">
              <Link to="/products" className="btn btn-primary">
                Shop Attire
              </Link>
              <Link to="/products?category=accessories" className="btn btn-secondary">
                Shop Accessories
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ===== TRUST BADGES ===== */}
      <TrustBadges />

      {/* ===== EDITORIAL FEATURES - 3 Stories ===== */}
      <EditorialFeatures />

      {/* ===== SHOP BY CULTURE ===== */}
      <CultureCards />

      {/* ===== CURATED COLLECTIONS ===== */}
      <section className="curated-collections section section--alt">
        <div className="container">
          <h2 className="text-center mb-xl">Curated Collections</h2>
          <div className="collections-grid">
            <Link to="/products?collection=ceremony-ready" className="collection-card">
              <div className="collection-card__background ceremony-ready"></div>
              <div className="collection-card__content">
                <h3>Ceremony Ready</h3>
                <p>Umembeso, Weddings, Heritage Day celebrations</p>
                <span className="collection-card__cta">Shop Collection →</span>
              </div>
            </Link>

            <Link to="/products?collection=everyday-heritage" className="collection-card">
              <div className="collection-card__background everyday"></div>
              <div className="collection-card__content">
                <h3>Everyday Heritage</h3>
                <p>Daily wear that tells your cultural story</p>
                <span className="collection-card__cta">Shop Collection →</span>
              </div>
            </Link>

            <Link to="/products?sort=popular" className="collection-card">
              <div className="collection-card__background bestsellers"></div>
              <div className="collection-card__content">
                <h3>Bestsellers</h3>
                <p>Customer favorites & community treasures</p>
                <span className="collection-card__cta">Shop Best Sellers →</span>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* ===== SIGNUP CTA ===== */}
      {!isShopper() && (
        <section className="signup-cta section">
          <div className="container">
            <div className="signup-card">
              <h2>Join Our Heritage Community</h2>
              <p>Create an account to unlock exclusive features:</p>
              <div className="benefits-grid">
                <div className="benefit">
                  <span className="benefit__icon">❤️</span>
                  <span>Save favorites</span>
                </div>
                <div className="benefit">
                  <span className="benefit__icon">📦</span>
                  <span>Track orders</span>
                </div>
                <div className="benefit">
                  <span className="benefit__icon">💾</span>
                  <span>Faster checkout</span>
                </div>
                <div className="benefit">
                  <span className="benefit__icon">📊</span>
                  <span>Recommendations</span>
                </div>
              </div>
              <button 
                className="btn btn-primary mt-lg"
                onClick={() => navigate('/login')}
              >
                Create Account Today
              </button>
              <p className="text-center text-small mt-md">
                Already have an account? <Link to="/login">Sign In Here</Link>
              </p>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

export default Home;
